import {
  Input,
  Button,
  Typography,
} from "@material-tailwind/react";
import Toaster from "../../components/Toaster";
import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ShowLoader from "@/components/Loader";
import Select from 'react-select';
import { ArrowLeftIcon, MinusCircleIcon, PlusCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { addAttribute, resetStatus } from "@/store/slices/attributeSlice";
import ScrollToTop from "@/components/ScrollToTop";
import { useTranslation } from "react-i18next";

const notBlankRegex = /\S/;
const noHTMLRegex = /^(?!.*<[^>]*>).*$/;
const noCodeRegex = /^(?!.*(javascript:|<script|<\/script>|<style|<\/style>|<iframe|<\/iframe>)).*$/i;
const noConsecutiveSpecialChars = /^(?!.*([!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])\1).*$/;
const noLeadingTrailingPunct = /^(?!^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])(?<![!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]$).*$/;
const  noLeadingTrailingPunctForCode = /^(?!^[!@$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])(?<![!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]$).*$/;

const getValidationSchema = (t) => yup.object().shape({
  name: yup
    .string()
    .trim()
    .required(t("validations.name.required"))
    .min(3, t("validations.name.min"))
    .max(255, t("validations.name.max"))
    .matches(notBlankRegex, t("validations.name.notBlank"))
    .matches(noHTMLRegex, t("validations.name.noHTML"))
    .matches(noCodeRegex, t("validations.name.noCode"))
    .matches(noConsecutiveSpecialChars, t("validations.name.noConsecutiveSpecialChars"))
    .matches(noLeadingTrailingPunct, t("validations.name.noLeadingTrailingPunct")),

  status: yup
    .string()
    .oneOf(["active", "inactive"], t("validations.status.oneOf"))
    .required(t("validations.status.required")),

  values: yup.array()
    .of(
      yup.object().shape({
        value: yup
          .string()
          .trim()
          .required(t("validations.values.value.required"))
          .min(1, t("validations.values.value.min"))
          .max(255, t("validations.values.value.max"))
          .matches(notBlankRegex, t("validations.values.value.notBlank"))
          .matches(noHTMLRegex, t("validations.values.value.noHTML"))
          .matches(noCodeRegex, t("validations.values.value.noCode"))
          .matches(noConsecutiveSpecialChars, t("validations.values.value.noConsecutiveSpecialChars"))
          .matches(noLeadingTrailingPunct, t("validations.values.value.noLeadingTrailingPunct")),

        code: yup
          .string()
          .nullable()
          .trim()
          .min(1, t("validations.values.code.min"))
          .max(20, t("validations.values.code.max"))
          .matches(noHTMLRegex, t("validations.values.code.noHTML"))
          .matches(noCodeRegex, t("validations.values.code.noCode"))
          .matches(noConsecutiveSpecialChars, t("validations.values.code.noConsecutiveSpecialChars"))
          .matches(noLeadingTrailingPunctForCode, t("validations.values.code.noLeadingTrailingPunct")),
      })
    )
    .min(1, t("validations.values.min")),
});

const AddAttribute = ({ isEdit = false, isView = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { t } = useTranslation("attribute");
  const { t: ct } = useTranslation("common");
  const { loading } = useSelector((state) => state.attribute);
  const selectedAttribute = useSelector((state) => state.attribute.selected);
  const statusOptions = [
    { label: t('statusOptions.active'), value: 'active' },
    { label: t('statusOptions.inactive'), value: 'inactive' }
  ];

  const defaultValues = {
    name: "",
    status: "active",
    values: [{ value: "", code: "" }],
  };

  const { register, handleSubmit, setValue, watch, control, reset, formState: { errors } } = useForm({
    resolver: yupResolver(getValidationSchema(t)),
    defaultValues,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "values",
  });

  useEffect(() => {
    if (isEdit) {
      if (selectedAttribute && String(selectedAttribute.id) === String(id)) {
        reset({ ...selectedAttribute });
      }
    } else {
      reset(defaultValues);
    }
  }, [isEdit, id, selectedAttribute, dispatch]);

  const onSubmit = async (data) => {
    try {
      const result = await dispatch(addAttribute(data)).unwrap();
      if (result.status) {
        Toaster.success(isEdit ? t('toaster.updateSuccess') : t('toaster.addSuccess'));
        dispatch(resetStatus());
        reset();
        navigate(`/dashboard/attributes`);
      } else {
        Toaster.error(result.message || t("toaster.somethingWentWrong"));
        dispatch(resetStatus());
      }
    } catch (err) {
      Toaster.error(err?.message || (isEdit ? t("toaster.updateFailed") : t("toaster.addFailed")));
      dispatch(resetStatus());
    }
  };

  return (
    <div className="mt-5 mb-8 flex flex-col gap-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {loading && <ShowLoader />}
      <ScrollToTop />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
        <div className="sticky top-0 z-30 mt-1 mb-6 p-5 bg-blue-gray-f5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-2 ">
            <div className="flex items-center gap-2 cursor-pointer">
              <Typography variant="h5" color="blue-gray" className="font-extrabold text-gray-800">
                {isView ? t('viewAttribute') : isEdit ? t('editAttribute') : t('addAttribute')}
              </Typography>
            </div>
            {!isView &&
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate(`/dashboard/attributes`)}
                  className="rounded-lg"
                >
                  {ct('cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white btn-material">
                  {loading ? ct("saving") : isEdit ? ct("update") : ct("save")}
                </Button>
              </div>
            }
          </div>
          <div className="flex items-center gap-1">
            <Link to={`/dashboard/attributes`} className="flex items-center text-blue-600 hover:text-blue-800">
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              {ct('back')}
            </Link>
          </div>
        </div>
        <fieldset disabled={isView}>
          <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white ">
            <div className="mb-5 pb-2 border-b border-blue-gray-200">
              <Typography variant="h4" color="blue-gray">
                {t('attributeDetails')}
              </Typography>
            </div>
            <div className="space-y-4">
              <div className="grid md:grid-cols-1 gap-4">
                <div>
                  <Typography
                    variant="small"
                    color="blue-gray"
                    className="mb-3 font-medium"
                  >
                    {t('attributeName')}<span className="text-red-500">*</span>
                  </Typography>
                  <Input
                    type="text"
                    placeholder={t('attributeNamePlaceholder')}
                    {...register("name")}
                    disabled={loading}
                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                    labelProps={{
                      className: "before:content-none after:content-none",
                    }}
                  />
                  <p className="text-red-500 text-xs mt-1">{errors.name?.message}</p>
                </div>
                <div>
                  <Typography
                    variant="small"
                    color="blue-gray"
                    className="mb-3 font-medium"
                  >
                    {t('status')} <span className="text-red-500">*</span>
                  </Typography>
                  <Select
                    type="text"
                    size="lg"
                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                    labelProps={{
                      className: "before:content-none after:content-none",
                    }}
                    placeholder={t('selectStatus')}
                    onChange={(status) => {
                      setValue('status', status?.value, { shouldValidate: true });
                    }}
                    value={statusOptions?.find(opt => opt.value === watch('status')) || null}
                    options={statusOptions}
                    error={Boolean(errors.status)}
                    isClearable
                    isDisabled={isView}
                  />
                  <p className="text-red-500 text-xs mt-1">{errors.status?.message}</p>
                </div>
              </div>
              <div>
                <Typography
                  variant="small"
                  color="blue-gray"
                  className="mb-3 font-medium"
                >
                  {t('attributeValue')} <span className="text-red-500">*</span>
                </Typography>

                {fields.map((field, index) => (
                  <div key={field.id} className="flex gap-2 items-baseline mb-2 ">
                    <div>
                      <Input
                        placeholder={t('attributeValuePlaceholder')}
                        {...register(`values.${index}.value`)}
                        disabled={loading}
                        className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                        labelProps={{
                          className: "before:content-none after:content-none",
                        }}
                      />
                      {errors.values && <p className="text-red-500 text-xs">
                        {errors.values?.[index]?.value?.message}
                      </p>}
                    </div>
                    <div>
                      <Input
                        placeholder={t('codePlaceholder')}
                        {...register(`values.${index}.code`)}
                        disabled={loading}
                        className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                        labelProps={{
                          className: "before:content-none after:content-none",
                        }}
                      />
                      {errors.values && <p className="text-red-500 text-xs">
                        {errors.values?.[index]?.code?.message}
                      </p>}
                    </div>
                    {!isView && <>
                      {index === 0 ? (
                        <PlusCircleIcon
                          className="h-6 w-6 text-green-600 cursor-pointer mb-2"
                          onClick={() => append({ value: "", code: "" })}
                        />
                      ) : (
                        <MinusCircleIcon
                          className="h-6 w-6 text-red-600 cursor-pointer mb-2"
                          onClick={() => remove(index)}
                        />
                      )}
                    </>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </fieldset>
      </form>
    </div>
  );
};

export default AddAttribute;
